import { makeAutoObservable, runInAction } from "mobx";
import { THROW_ON_ERROR } from "shared/dialogSubmit";
import type RootStore from "stores/RootStore";
import { isOperator } from "auth/tier";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  normalizePipeline,
  stripRefPrefix,
  type DevOpsConnection,
  type DevOpsPipelineApiRecord,
  type DevOpsProject,
  type DevOpsPipeline,
  type DevOpsQueueBuildPayload,
  type DevOpsCancelBuildPayload,
  type DevOpsEvent,
  type DevOpsOverviewClientRef,
} from "../devops.types";
import type DevOpsScopeStore from "../shared/DevOpsScopeStore";
import { swallowedFailure } from "../shared/swallowedFind";
import { clientsForProject, type ProjectClientsView } from "../shared/projectClients";
import { subscribeDevOpsEvents } from "../devopsLiveStatus";
import { exportToCsv } from "shared/common/utils/export";
import {
  PIPELINE_EXPORT_COLUMNS,
  slugForFileName,
  toPipelineExportRow,
  type DevOpsExportScope,
  type ExportRecord,
} from "../devopsExports";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/** Board status filter — the buckets the summary cards count. */
export type PipelineStatusFilter = "all" | "succeeded" | "failed" | "running";

export type PipelineSortKey =
  | "name"
  | "path"
  | "latestStatus"
  | "latestBuildNumber"
  | "latestSourceBranch"
  | "latestFinishTime";

/** Sort keys that read best newest-first on first click. */
const DESC_FIRST: ReadonlySet<PipelineSortKey> = new Set(["latestFinishTime"]);

/**
 * Normalise a ServiceStore `find()` response into a plain array of rows.
 * Paginated services return `{ data, total }`; custom non-paginated finds may
 * return the bare array — tolerate both. (Replaces the removed `unwrapList`.)
 */
function rowsOf<T>(result: { data?: T[] } | T[] | null | undefined): T[] {
  if (Array.isArray(result)) return result;
  return result?.data ?? [];
}

// Azure BuildResult enum codes (the wire values) used to translate the numeric
// `result` carried on a live `devops-event` into the flat board status.
const AZ_BUILD_RESULT_TO_STATUS: Record<number, DevOpsPipeline["latestStatus"]> = {
  2: "succeeded",
  4: "partiallySucceeded",
  8: "failed",
  32: "canceled",
};

/**
 * View store for the read-only Pipelines status board.
 *
 * Plain class with `makeAutoObservable` (route ViewStores never extend
 * ViewStore). Backed by registered `@intrasync/stores` ServiceStores
 * (`devops-connections`, `devops-projects`, `devops-pipelines`). This phase is
 * READ-ONLY: it lists connections, then their live Azure DevOps projects + build
 * definitions (with latest status). No write actions to Azure are issued here
 * (queue/cancel are Phase 3).
 *
 * The connection list comes from the `devops-connections` ServiceStore's entity
 * cache (a computed getter). `projects`/`pipelines` are live Azure proxies that
 * are re-queried per connection/project selection; because the ServiceStore
 * entity cache accumulates across queries, those scoped + normalised rows are
 * read from each `find()` RESPONSE and kept as local view projections, not the
 * cross-query cache.
 *
 * WHICH connection/project is NOT this store's fact. It reads and writes the
 * shared `devopsScopeStore` (see shared/DevOpsScopeStore.ts) — the one
 * selection every board, Project 360 and the Overview agree on — so choosing
 * a project here is the choice on every other board too. `selectedConnectionId`
 * / `selectedProjectId` are read-through computeds over it; the header's
 * selectors still call `selectConnection` / `selectProject`.
 */
class PipelinesBoardStore {
  rootStore: RootStore;

  projects: DevOpsProject[] = [];
  pipelines: DevOpsPipeline[] = [];

  connectionsLoading = false;
  projectsLoading = false;
  pipelinesLoading = false;
  error: string | null = null;

  // Header filters (applied client-side over the loaded board).
  searchQuery = "";
  statusFilter: PipelineStatusFilter = "all";

  // Column sort (applied client-side over the filtered rows, like the filters)
  sortKey: PipelineSortKey = "name";
  sortDir: SortDir = "asc";

  // ---- Phase 3: pipeline write actions ----
  /** Pipeline (definition) ids with an in-flight queue/cancel action. */
  actioningPipelineIds: string[] = [];
  actionError: string | null = null;

  // ---- Phase 3: live status ----
  /** Disposer returned by the live `devops-event` subscription. */
  private unsubscribeLive: (() => void) | null = null;

  /**
   * Sequence stamps for the two SCOPED reads — the same discipline as
   * `DevOpsSubscriptionsStore.projectsSeq` and `DevOpsProjectStore.loadSeq`.
   * Project A is chosen, then B. B answers first and paints; A's slower
   * answer must NOT repaint the grid while the shared scope says B, or the
   * page would offer B's project beside A's rows and an action would combine
   * the two. A superseded read writes nothing at all — not the rows, not its
   * error, and not the spinner its `finally` would otherwise clear out from
   * under the read that is still running.
   */
  private projectsSeq = 0;
  private pipelinesSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- ServiceStore accessors ----

  private get connectionsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("devops-connections") ?? this.rootStore.devopsConnectionsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  private get projectsService(): ServiceStoreType | null {
    return (this.rootStore.devopsProjectsStore as ServiceStoreType | null) ?? null;
  }

  private get pipelinesService(): ServiceStoreType | null {
    return (this.rootStore.devopsPipelinesStore as ServiceStoreType | null) ?? null;
  }

  /** Connection list — computed over the `devops-connections` entity cache. */
  get connections(): DevOpsConnection[] {
    return (this.connectionsService?.entities ?? []) as unknown as DevOpsConnection[];
  }

  // ---- The shared scope ----

  /** THE selection (app store `devopsScopeStore`); null only in a bare test root. */
  private get scopeStore(): DevOpsScopeStore | null {
    return (this.rootStore.devopsScopeStore as DevOpsScopeStore | undefined) ?? null;
  }

  get selectedConnectionId(): string | null {
    return this.scopeStore?.connectionId ?? null;
  }

  get selectedProjectId(): string | null {
    return this.scopeStore?.projectId ?? null;
  }

  /**
   * The clients the scoped project is linked to, from whichever
   * `devops-overview` response is loaded (never fetched from here). `known`
   * is false until one covers this connection, and the strip stays silent.
   */
  get projectClients(): ProjectClientsView {
    return clientsForProject(
      this.rootStore,
      this.selectedConnectionId,
      this.selectedProjectId
    );
  }

  get clientsForCurrentProject(): DevOpsOverviewClientRef[] {
    return this.projectClients.clients;
  }

  // ---- Derived ----

  /**
   * Whether the current user may queue/cancel builds (the operator/admin tier).
   * The API is the real enforcement point (devops RBAC `requireActionAccess`);
   * this only governs whether the buttons render. Tier resolution is centralised
   * in `auth/tier.ts` (reads the API-computed `currentUser.tier`) — widen there,
   * never at the call sites.
   */
  get canManage(): boolean {
    return isOperator(this.rootStore);
  }

  isActioning(pipelineId: string): boolean {
    return this.actioningPipelineIds.includes(pipelineId);
  }

  get selectedConnection(): DevOpsConnection | null {
    if (!this.selectedConnectionId) return null;
    return (
      this.connections.find((c) => c.id === this.selectedConnectionId) || null
    );
  }

  get selectedProject(): DevOpsProject | null {
    if (!this.selectedProjectId) return null;
    return this.projects.find((p) => p.id === this.selectedProjectId) || null;
  }

  get totalCount(): number {
    return this.pipelines.length;
  }

  /** How many pipelines survive the header filters — what the table renders. */
  get filteredCount(): number {
    return this.filteredPipelines.length;
  }

  /**
   * The sentence the board prints under the table, or null when nothing is
   * loaded.
   *
   * `devops-pipelines.find` calls Azure's build.getDefinitions(project) and
   * returns whatever that hands back. Azure pages definitions server-side with
   * a continuation token which this proxy does not follow, so a project with a
   * very large number of pipelines shows its first page only — and nothing in
   * the response says which case you are in. Say so rather than let the board
   * read as the definitive list.
   */
  get upstreamPagingNote(): string | null {
    if (this.totalCount === 0) return null;
    return `Showing the ${this.totalCount} pipeline${
      this.totalCount === 1 ? "" : "s"
    } Azure DevOps returned for this project. Azure pages build definitions server-side, so a very large project may list only the first page.`;
  }

  // ---- CSV export ---------------------------------------------------------

  /** The connection/project this board was read through, for the export scope
   *  columns — a board CSV with no scope column is uninterpretable later. */
  get exportScope(): DevOpsExportScope {
    return {
      connectionName: this.selectedConnection?.name ?? "",
      projectName: this.selectedProject?.name ?? this.selectedProjectId ?? "",
    };
  }

  /**
   * The board as flat CSV records — exactly what is ON SCREEN
   * (`sortedPipelines` = status filter + name search, then the active sort).
   */
  get exportRows(): ExportRecord[] {
    const scope = this.exportScope;
    return this.sortedPipelines.map((p) => toPipelineExportRow(p, scope));
  }

  get canExport(): boolean {
    return this.exportRows.length > 0;
  }

  /** Download the on-screen board as CSV. False (never a throw) on an empty
   *  set; the header also disables the button on {@link canExport}. */
  exportCsv(): boolean {
    const rows = this.exportRows;
    if (rows.length === 0) return false;
    try {
      const project = this.exportScope.projectName || "pipelines";
      exportToCsv(rows, PIPELINE_EXPORT_COLUMNS, {
        fileName: `pipelines-${slugForFileName(project)}-${new Date()
          .toISOString()
          .slice(0, 10)}`,
      });
      return true;
    } catch (err) {
      console.error("Pipelines CSV export failed:", err);
      return false;
    }
  }

  get succeededCount(): number {
    return this.pipelines.filter((p) => p.latestStatus === "succeeded").length;
  }

  get failedCount(): number {
    return this.pipelines.filter(
      (p) => p.latestStatus === "failed" || p.latestStatus === "canceled"
    ).length;
  }

  get runningCount(): number {
    return this.pipelines.filter(
      (p) => p.latestState === "inProgress" || p.latestStatus === "inProgress"
    ).length;
  }

  /**
   * Board rows after the status filter + name search. The status buckets match
   * the summary-card counts exactly (failed includes canceled; running matches
   * in-progress state OR status), so a card click filters to the rows it counts.
   */
  get filteredPipelines(): DevOpsPipeline[] {
    let result = this.pipelines;

    if (this.statusFilter === "succeeded") {
      result = result.filter((p) => p.latestStatus === "succeeded");
    } else if (this.statusFilter === "failed") {
      result = result.filter(
        (p) => p.latestStatus === "failed" || p.latestStatus === "canceled"
      );
    } else if (this.statusFilter === "running") {
      result = result.filter(
        (p) => p.latestState === "inProgress" || p.latestStatus === "inProgress"
      );
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      result = result.filter((p) => p.name?.toLowerCase().includes(q));
    }

    return result;
  }

  /**
   * The rows the table renders: filters, then the active column sort. Nulls
   * (e.g. "never finished") sort last in either direction.
   */
  get sortedPipelines(): DevOpsPipeline[] {
    const key = this.sortKey;
    const valueOf = (p: DevOpsPipeline): SortValue => {
      switch (key) {
        case "name":
          return textValue(p.name);
        case "path":
          return textValue(p.path);
        case "latestStatus":
          return textValue(p.latestStatus);
        case "latestBuildNumber":
          return textValue(p.latestBuildNumber);
        case "latestSourceBranch":
          return textValue(stripRefPrefix(p.latestSourceBranch));
        case "latestFinishTime":
          return dateValue(p.latestFinishTime);
      }
    };
    return sortRows(this.filteredPipelines, valueOf, this.sortDir);
  }

  // ---- Filter / sort setters ----

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  /** Set directly or via a summary-card click (cards map to the same buckets). */
  setStatusFilter(filter: PipelineStatusFilter) {
    this.statusFilter = filter;
  }

  /** Column-header click: same column toggles direction, a new column starts
   *  in its natural direction (finish time newest-first, text A→Z). */
  setSort(key: PipelineSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  // ---- Loading ----

  /**
   * Entry point from the route (and from a Project 360 tab). A scope chosen
   * anywhere else is honoured as-is; only an EMPTY scope auto-picks the first
   * connection (and, through `selectConnection`, its default project).
   */
  async initialize() {
    this.startLiveStatus();
    const ended = sessionMark(this.rootStore);
    await this.loadConnections();
    // A sign-out during the read leaves `error` null (it is no failure of the
    // next person's), so the `error` check below cannot end the sequence.
    if (ended()) return;
    // A connection remembered from a previous session may have been removed
    // since: drop it (and its project) so the empty-scope auto-pick below
    // runs instead of a doomed project read. Never after a FAILED connections
    // load — `loadConnections` records it on `error`, and an empty list from
    // an error says nothing about the remembered connection.
    // A failed connections load ends the sequence. There is no list to
    // reconcile against and no connection to read a project for, and going on
    // would replace the CAUSAL error with whatever the doomed project read
    // reports — telling the operator the projects are unavailable when the
    // truth is that HQ could not reach the connections at all.
    if (this.error) return;
    this.scopeStore?.reconcileConnections(this.connections.map((c) => c.id));
    if (this.selectedConnectionId) {
      await this.adoptScope();
    } else if (this.connections.length > 0) {
      await this.selectConnection(this.connections[0].id);
    }
  }

  /**
   * Load this board for the scope somebody else set (Project 360, the
   * Overview's card click, another board's header, last session). Projects are
   * loaded for the scope's connection; a project id that connection no longer
   * lists is cleared — the PROJECT only, the connection stays — and nothing is
   * picked in its place: the scope was a deliberate choice, not this board's
   * guess. A failed project read leaves the scope alone (the error shows).
   */
  private async adoptScope() {
    const ended = sessionMark(this.rootStore);
    await this.loadProjects();
    // As in initialize: a sign-out leaves no error, and going on would clear
    // the shared scope's project against the list reset() emptied.
    if (ended() || this.error) return;
    const projectId = this.selectedProjectId;
    if (!projectId) {
      runInAction(() => {
        this.pipelines = [];
      });
      return;
    }
    const project = this.projects.find((p) => p.id === projectId);
    if (!project) {
      runInAction(() => {
        this.scopeStore?.setProject(null);
        this.pipelines = [];
      });
      return;
    }
    // Fill in the display name when the scope was set by id alone.
    if (!this.scopeStore?.projectName) {
      this.scopeStore?.setScope({ projectName: project.name });
    }
    await this.loadPipelines();
  }

  async loadConnections() {
    this.connectionsLoading = true;
    this.error = null;
    try {
      // ServiceStore.find populates its entity cache, which `connections` reads.
      const result = await this.connectionsService?.find({
        query: { $limit: 200, $sort: { name: 1 } },
      });
      // A null result is a SWALLOWED rejection (see shared/swallowedFind.ts),
      // never an org with no connections. It matters twice over here: the
      // cache is empty on a fresh session (`enableStorage: false`), so a
      // silent failure would hand `initialize`'s reconcile an empty list and
      // wipe the remembered scope — the one thing reconcileConnections is
      // documented never to do on a failed load.
      if (result == null) {
        throw swallowedFailure(this.connectionsService, "errorOnFind", "Failed to load connections");
      }
    } catch (err: unknown) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : "Failed to load connections";
      });

      console.error("Failed to load DevOps connections:", err);
    } finally {
      runInAction(() => {
        this.connectionsLoading = false;
      });
    }
  }

  /** Header selector — writes the shared scope (a new connection clears the project). */
  async selectConnection(connectionId: string | null) {
    runInAction(() => {
      // Invalidate both reads in flight for the scope being left. The loaders
      // below stamp their own sequence when a selection IS made, but clearing
      // one loads nothing — without this bump the previous scope's answer
      // still matches and paints itself onto a board that has moved on.
      this.projectsSeq += 1;
      this.pipelinesSeq += 1;
      this.projectsLoading = false;
      this.pipelinesLoading = false;
      this.scopeStore?.setScope({ connectionId, projectId: null, projectName: null });
      this.projects = [];
      this.pipelines = [];
    });
    if (connectionId) {
      await this.loadProjects();
      // Auto-select the connection's default project (or the first one).
      const conn = this.selectedConnection;
      const defaultProj =
        (conn?.defaultProject &&
          this.projects.find(
            (p) =>
              p.name === conn.defaultProject || p.id === conn.defaultProject
          )) ||
        this.projects[0];
      if (defaultProj) {
        await this.selectProject(defaultProj.id);
      }
    }
  }

  async loadProjects() {
    if (!this.selectedConnectionId) return;
    const seq = ++this.projectsSeq;
    this.projectsLoading = true;
    try {
      // Live read-only proxy to Azure DevOps via the connection. Scoped per
      // connection and re-queried on selection change, so read the find()
      // response rows rather than the cross-query entity cache.
      // No `$limit`: `devops-projects.find` reads only `connectionId` and
      // calls Azure's core.getProjects(), returning whatever page Azure gives
      // back. A `$limit` here was inert — it looked like an enforced cap and
      // was not one. Azure's own paging is the real bound; if an org ever has
      // more projects than one page, the picker needs a search, not a number.
      const result = await this.projectsService?.find({
        query: { connectionId: this.selectedConnectionId },
      });
      // The wrapper swallows a rejection into a null result: that is a FAILED
      // read (see shared/swallowedFind.ts), never an org with no projects —
      // and must not let adoptScope conclude the selected project is gone.
      if (seq !== this.projectsSeq) return; // a newer scope superseded this
      if (result == null) {
        throw swallowedFailure(this.projectsService, "errorOnFind", "Failed to load projects");
      }
      runInAction(() => {
        this.projects = rowsOf<DevOpsProject>(
          result as unknown as { data?: DevOpsProject[] } | DevOpsProject[],
        );
      });
    } catch (err: unknown) {
      // A superseded read reports nothing: its failure belongs to a scope
      // this board has left.
      if (seq !== this.projectsSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to load projects";
        }
      });

      console.error("Failed to load DevOps projects:", err);
    } finally {
      runInAction(() => {
        // Only the newest read owns the spinner.
        if (seq === this.projectsSeq) this.projectsLoading = false;
      });
    }
  }

  /** Header selector — writes the shared scope, name included for the other pages' headers. */
  async selectProject(projectId: string | null) {
    runInAction(() => {
      // Invalidate the board read in flight for the project being left.
      this.pipelinesSeq += 1;
      this.pipelinesLoading = false;
      this.scopeStore?.setProject(
        projectId,
        this.projects.find((p) => p.id === projectId)?.name ?? null
      );
      this.pipelines = [];
    });
    if (projectId) {
      await this.loadPipelines();
    }
  }

  async loadPipelines() {
    if (!this.selectedConnectionId || !this.selectedProjectId) return;
    const seq = ++this.pipelinesSeq;
    this.pipelinesLoading = true;
    this.error = null;
    try {
      // Live read-only proxy: build definitions + latest build status. The API
      // returns a bare array of records with a NESTED numeric-enum `latestBuild`;
      // normalise each into the flat board shape. Scoped + re-queried per
      // selection → read the find() response, not the entity cache.
      //
      // NO `$limit`, and dropping it raised nothing: `devops-pipelines.find`
      // reads connectionId/projectId and ignores the rest, so the old
      // `$limit: 500` was inert — a cap that looked enforced and was not. The
      // real bound is Azure's own definition paging; the board discloses it
      // (see {@link upstreamPagingNote}) instead of implying a local cap.
      const result = await this.pipelinesService?.find({
        query: {
          connectionId: this.selectedConnectionId,
          projectId: this.selectedProjectId,
        },
      });
      if (seq !== this.pipelinesSeq) return; // a newer project superseded this
      // A null result is a SWALLOWED rejection (see shared/swallowedFind.ts),
      // never a project with no build definitions. Left as-is an Azure 503
      // empties a populated grid with nothing saying the read failed.
      if (result == null) {
        throw swallowedFailure(this.pipelinesService, "errorOnFind", "Failed to load pipelines");
      }
      runInAction(() => {
        this.pipelines = rowsOf<DevOpsPipelineApiRecord>(
          result as unknown as
            | { data?: DevOpsPipelineApiRecord[] }
            | DevOpsPipelineApiRecord[],
        ).map(normalizePipeline);
      });
    } catch (err: unknown) {
      // A superseded read reports nothing: its failure belongs to a project
      // this board has left.
      if (seq !== this.pipelinesSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to load pipelines";
        }
      });

      console.error("Failed to load DevOps pipelines:", err);
    } finally {
      runInAction(() => {
        // Only the newest read owns the spinner.
        if (seq === this.pipelinesSeq) this.pipelinesLoading = false;
      });
    }
  }

  /** Header refresh — re-pull pipelines for the current selection. */
  async reload() {
    if (this.selectedConnectionId && this.selectedProjectId) {
      await this.loadPipelines();
    } else {
      await this.initialize();
    }
  }

  // ---- Phase 3: queue / cancel build actions --------------------------------

  private beginAction(pipelineId: string) {
    if (!this.actioningPipelineIds.includes(pipelineId)) {
      this.actioningPipelineIds = [...this.actioningPipelineIds, pipelineId];
    }
    this.actionError = null;
  }

  private endAction(pipelineId: string) {
    this.actioningPipelineIds = this.actioningPipelineIds.filter(
      (id) => id !== pipelineId
    );
  }

  /** Patch a single pipeline row in place (used by optimistic updates + events). */
  private patchPipeline(pipelineId: string, patch: Partial<DevOpsPipeline>) {
    this.pipelines = this.pipelines.map((p) =>
      p.id === pipelineId ? { ...p, ...patch } : p
    );
  }

  /**
   * Queue a new build for a pipeline (build definition). Admin/operator only —
   * the API enforces the gate; the UI hides the button otherwise. Optimistically
   * flips the row to "Running/Queued" then re-pulls. No secret is sent; the API
   * resolves the connection's encrypted PAT server-side.
   */
  async queueBuild(pipeline: DevOpsPipeline): Promise<void> {
    if (!this.selectedConnectionId || !this.selectedProjectId) return;
    this.beginAction(pipeline.id);
    // Optimistic: show the build as starting immediately.
    this.patchPipeline(pipeline.id, {
      latestStatus: "notStarted",
      latestState: "notStarted",
    });
    try {
      const payload: DevOpsQueueBuildPayload = {
        action: "queueBuild",
        connectionId: this.selectedConnectionId,
        // API reads `data.project` (not projectId) and requires a numeric
        // definitionId — convert the string row id.
        project: this.selectedProjectId,
        definitionId: Number(pipeline.id),
        sourceBranch: pipeline.latestSourceBranch,
      };
      // THROW_ON_ERROR: a refusal otherwise RESOLVES null and read as done.
      await this.pipelinesService?.create(payload as never, THROW_ON_ERROR);
      // Refresh the row from the source of truth (best-effort).
      await this.loadPipelines();
    } catch (err: unknown) {
      // A sign-out: no error, and no re-pull — it would go out in the next
      // person's session.
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.actionError =
          err instanceof Error ? err.message : "Failed to queue build";
      });
      // Roll back the optimistic flip by re-pulling.
      await this.loadPipelines();

      console.error("queueBuild failed:", err);
    } finally {
      runInAction(() => this.endAction(pipeline.id));
    }
  }

  /**
   * Cancel the latest in-progress build for a pipeline. Admin/operator only.
   * Optimistically flips the row to "Canceled" then re-pulls.
   */
  async cancelBuild(pipeline: DevOpsPipeline, buildId: string): Promise<void> {
    if (!this.selectedConnectionId || !this.selectedProjectId) return;
    this.beginAction(pipeline.id);
    this.patchPipeline(pipeline.id, {
      latestStatus: "canceled",
      latestState: "completed",
    });
    try {
      const payload: DevOpsCancelBuildPayload = {
        action: "cancelBuild",
        connectionId: this.selectedConnectionId,
        // API reads `data.project` (not projectId) and requires a numeric buildId.
        project: this.selectedProjectId,
        buildId: Number(buildId),
      };
      // THROW_ON_ERROR: a refusal otherwise RESOLVES null and read as done.
      await this.pipelinesService?.create(payload as never, THROW_ON_ERROR);
      await this.loadPipelines();
    } catch (err: unknown) {
      // A sign-out: no error, and no re-pull — it would go out in the next
      // person's session.
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.actionError =
          err instanceof Error ? err.message : "Failed to cancel build";
      });
      await this.loadPipelines();

      console.error("cancelBuild failed:", err);
    } finally {
      runInAction(() => this.endAction(pipeline.id));
    }
  }

  // ---- Phase 3: live status (Socket.io `devops-event`) ----------------------

  /**
   * Subscribe to the realtime `devops-event` channel and patch the matching
   * pipeline/build row in place — no full reload. Idempotent; safe to call from
   * `initialize()`. The disposer is held so the route's onExit can tear it down.
   */
  startLiveStatus() {
    if (this.unsubscribeLive) return;
    this.unsubscribeLive = subscribeDevOpsEvents(this.rootStore, (event) =>
      this.handleDevOpsEvent(event)
    );
  }

  /**
   * Apply a single live `devops-event` to the board. Ignores events for other
   * connections/projects, and work-item events (the WorkItems store handles
   * those). Translates the numeric Azure status/result into the flat board
   * shape. If a row for the event isn't loaded, falls back to a scoped re-pull.
   */
  handleDevOpsEvent(event: DevOpsEvent) {
    if (event.entity !== "build" && event.entity !== "pipeline") return;
    // Scope to the currently-open selection.
    if (
      (event.connectionId && event.connectionId !== this.selectedConnectionId) ||
      (event.projectId && event.projectId !== this.selectedProjectId)
    ) {
      return;
    }
    const definitionId =
      event.definitionId != null ? String(event.definitionId) : null;
    if (!definitionId) {
      // Can't map to a row — re-pull the current project as a fallback.
      void this.loadPipelines();
      return;
    }
    const row = this.pipelines.find((p) => p.id === definitionId);
    if (!row) {
      void this.loadPipelines();
      return;
    }

    const patch: Partial<DevOpsPipeline> = {};
    const statusNum =
      typeof event.status === "number" ? event.status : Number(event.status);
    const resultNum =
      typeof event.result === "number" ? event.result : Number(event.result);
    if (!Number.isNaN(statusNum)) {
      if (statusNum === 2 /* Completed */) {
        patch.latestState = "completed";
        patch.latestStatus =
          (!Number.isNaN(resultNum) && AZ_BUILD_RESULT_TO_STATUS[resultNum]) ||
          "none";
      } else if (statusNum === 1 /* InProgress */) {
        patch.latestState = "inProgress";
        patch.latestStatus = "inProgress";
      } else if (statusNum === 32 /* NotStarted */) {
        patch.latestState = "notStarted";
        patch.latestStatus = "notStarted";
      }
    } else if (!Number.isNaN(resultNum) && AZ_BUILD_RESULT_TO_STATUS[resultNum]) {
      patch.latestStatus = AZ_BUILD_RESULT_TO_STATUS[resultNum];
    }
    if (event.buildNumber) patch.latestBuildNumber = event.buildNumber;
    if (event.sourceBranch) patch.latestSourceBranch = event.sourceBranch;
    if (event.finishTime !== undefined) patch.latestFinishTime = event.finishTime;

    if (Object.keys(patch).length > 0) {
      runInAction(() => this.patchPipeline(definitionId, patch));
    }
  }

  /** Route onExit — tear down the live subscription. */
  dispose() {
    if (this.unsubscribeLive) {
      this.unsubscribeLive();
      this.unsubscribeLive = null;
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.dispose();
    this.projects = [];
    this.pipelines = [];
    this.connectionsLoading = false;
    this.projectsLoading = false;
    this.pipelinesLoading = false;
    this.error = null;
    this.searchQuery = "";
    this.statusFilter = "all";
    this.sortKey = "name";
    this.sortDir = "asc";
    this.actioningPipelineIds = [];
    this.actionError = null;
    this.unsubscribeLive = null;
    this.projectsSeq += 1;
    this.pipelinesSeq += 1;
  }
}

export default PipelinesBoardStore;
